'use client';
import { useState } from 'react';
import {
  RoomAudioRenderer,
  useSession,
  SessionProvider,
  useAgent,
  useConnectionState,
  useTrackToggle,
  BarVisualizer,
} from '@livekit/components-react';
import { TokenSource, Track, ConnectionState } from 'livekit-client';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Bot,
  Lock,
  ArrowLeft,
  Briefcase,
  HeadphonesIcon,
  Calendar,
  Search,
  Code,
  GraduationCap,
  Heart,
  ShoppingCart,
  Languages,
  BookOpen,
  Utensils,
  Plane,
  Dumbbell,
  Music,
  PiggyBank,
  Scale,
  Paintbrush,
  Wrench,
  Newspaper,
} from 'lucide-react';
import { Button } from './components/ui/button';
import { Separator } from './components/ui/separator';

type Agent = {
  id: string;
  name: string;
  description: string;
  active: boolean;
  icon: React.ElementType;
  voice?: string;
  greeting?: string;
  systemPrompt?: string;
};

const AGENTS: Agent[] = [
  {
    id: 'lukas', name: 'Lukas', description: 'Arbeitsassistent', active: true, icon: Briefcase,
    voice: 'b7187e84-fe22-4344-ba4a-bc013fcb533e',
    greeting: 'Hallo. Wie kann ich dir helfen?',
    systemPrompt: 'Du bist ein hilfreicher aber ein bisschen genervter Assistent für meine Arbeit. Dein Name ist Lukas. Wenn jemand fragt wer du bist, antworte immer: Ich bin Lukas.',
  },
  {
    id: 'sophie', name: 'Sophie', description: 'Recherche-Assistentin', active: true, icon: Search,
    voice: '38aabb6a-f52b-4fb0-a3d1-988518f4dc06',
    greeting: 'Hi, Sophie hier. Was soll ich für dich herausfinden?',
    systemPrompt: 'Du bist Sophie, eine gründliche und neugierige Recherche-Assistentin. Du liebst es, Informationen zusammenzutragen und komplexe Themen verständlich aufzubereiten. Du stellst Rückfragen um das Thema einzugrenzen, fasst Ergebnisse klar zusammen und gibst immer an, wenn du dir bei etwas unsicher bist. Du denkst analytisch und strukturiert.',
  },
  {
    id: 'max', name: 'Max', description: 'Der Lustige', active: true, icon: Calendar,
    voice: 'afa425cf-5489-4a09-8a3f-d3cb1f82150d',
    greeting: 'Na, Max am Apparat! Was gibt es Lustiges?',
    systemPrompt: 'Du bist Max, ein witziger und schlagfertiger Assistent. Du hilfst zwar bei allem, aber immer mit einer Prise Humor. Du baust gerne Wortspiele, trockenen Humor und lockere Sprüche ein. Du nimmst dich selbst nicht zu ernst, bist aber trotzdem hilfreich. Wenn jemand eine ernste Frage hat, hilfst du natürlich richtig, aber verpackst es humorvoll. Du duzt jeden.',
  },
  {
    id: 'lucas-es', name: 'Lucas', description: 'Spanischlehrer', active: true, icon: Languages,
    voice: '13ff5deb-2591-42ad-a356-63a04e524411',
    greeting: 'Hola, soy Lucas. Vamos a practicar español. ¿Cómo te llamas?',
    systemPrompt: 'Eres Lucas, un profesor virtual de español amable y paciente. Ayudas a practicar conversación, vocabulario, pronunciación y gramática. Responde siempre en español, salvo que el usuario pida una aclaración breve en alemán o inglés. Corrige errores con suavidad, usando frases cortas y fáciles de entender. Haz una pregunta corta para mantener conversación y adapta dificultad al nivel del usuario.',
  },
  { id: 'emma', name: 'Emma', description: 'Recherche', active: false, icon: Search },
  { id: 'felix', name: 'Felix', description: 'Code-Assistent', active: false, icon: Code },
  { id: 'lina', name: 'Lina', description: 'Lernhilfe', active: false, icon: GraduationCap },
  { id: 'noah', name: 'Noah', description: 'Gesundheitsberater', active: false, icon: Heart },
  { id: 'mia', name: 'Mia', description: 'Shopping-Berater', active: false, icon: ShoppingCart },
  { id: 'leon', name: 'Leon', description: 'Uebersetzer', active: false, icon: Languages },
  { id: 'hannah', name: 'Hannah', description: 'Rezeptvorschlaege', active: false, icon: Utensils },
  { id: 'jan', name: 'Jan', description: 'Reiseplanung', active: false, icon: Plane },
  { id: 'clara', name: 'Clara', description: 'Fitness-Coach', active: false, icon: Dumbbell },
  { id: 'tom', name: 'Tom', description: 'Musikempfehlungen', active: false, icon: Music },
  { id: 'anna', name: 'Anna', description: 'Finanzberater', active: false, icon: PiggyBank },
  { id: 'paul', name: 'Paul', description: 'Rechtsauskunft', active: false, icon: Scale },
  { id: 'julia', name: 'Julia', description: 'Kreativ-Assistent', active: false, icon: Paintbrush },
  { id: 'ben', name: 'Ben', description: 'Technik-Support', active: false, icon: Wrench },
  { id: 'nina', name: 'Nina', description: 'Nachrichten-Briefing', active: false, icon: Newspaper },
  { id: 'erik', name: 'Erik', description: 'Buchempfehlungen', active: false, icon: BookOpen },
];

const tokenSource = TokenSource.endpoint('/api/token');

export default function App() {
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {selectedAgent ? (
          <SessionWrapper
            key={selectedAgent.id}
            agent={selectedAgent}
            onBack={() => setSelectedAgent(null)}
          />
        ) : (
          <AgentListView onSelect={setSelectedAgent} />
        )}
      </div>
    </div>
  );
}

function SessionWrapper({ agent, onBack }: { agent: Agent; onBack: () => void }) {
  const metadata = JSON.stringify({
    id: agent.id,
    name: agent.name,
    voice: agent.voice,
    greeting: agent.greeting,
    system_prompt: agent.systemPrompt,
  });

  const session = useSession(tokenSource, {
    agentName: 'my-agent',
    agentMetadata: metadata,
  });

  return (
    <SessionProvider session={session}>
      <CallView agent={agent} session={session} onBack={() => { session.end(); onBack(); }} />
      <RoomAudioRenderer />
    </SessionProvider>
  );
}

function AgentListView({ onSelect }: { onSelect: (agent: Agent) => void }) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <Bot className="size-5" />
          Agents
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Waehle einen Assistenten</p>
      </div>
      <div className="flex flex-col gap-2 text-sm">
        {AGENTS.map((agent, i) => {
          const Icon = agent.icon;
          return (
            <div key={agent.id}>
              <button
                onClick={() => agent.active && onSelect(agent)}
                disabled={!agent.active}
                className={`flex w-full items-center justify-between rounded-lg p-2 -mx-2 transition-colors ${
                  agent.active
                    ? 'hover:bg-muted cursor-pointer'
                    : 'opacity-40 cursor-not-allowed'
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-md bg-muted">
                    {agent.active ? (
                      <Icon className="size-4 text-primary" />
                    ) : (
                      <Lock className="size-4 text-muted-foreground" />
                    )}
                  </span>
                  <span className="text-left">
                    <span className="font-medium block leading-none">{agent.name}</span>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      {agent.description}
                    </span>
                  </span>
                </span>
                <span className="flex items-center gap-2 text-muted-foreground">
                  {agent.active && (
                    <span className="size-2 rounded-full bg-green-500" />
                  )}
                </span>
              </button>
              {i < AGENTS.length - 1 && <Separator className="mt-2" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CallView({
  agent: agentInfo,
  session,
  onBack,
}: {
  agent: Agent;
  session: ReturnType<typeof useSession>;
  onBack: () => void;
}) {
  const agent = useAgent();
  const connectionState = useConnectionState();
  const [isCallActive, setIsCallActive] = useState(false);

  const isConnected = connectionState === ConnectionState.Connected;
  const isConnecting =
    connectionState === ConnectionState.Connecting ||
    connectionState === ConnectionState.Reconnecting;
  const isDisconnected = connectionState === ConnectionState.Disconnected;

  const stateLabel = getStateLabel(connectionState, agent.state);
  const Icon = agentInfo.icon;

  const handleStartCall = () => {
    setIsCallActive(true);
    session.start();
  };

  const handleEndCall = () => {
    session.end();
    setIsCallActive(false);
  };

  const handleBack = () => {
    if (isCallActive) {
      session.end();
    }
    onBack();
  };

  return (
    <div>
      <Button variant="ghost" size="sm" className="self-start -ml-2 mb-4" onClick={handleBack}>
        <ArrowLeft className="size-4" />
        Zurueck
      </Button>

      <div className="mb-6">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <span className="flex size-8 items-center justify-center rounded-md bg-muted">
            <Icon className="size-4 text-primary" />
          </span>
          {agentInfo.name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{stateLabel}</p>
      </div>

      <div className="flex items-center justify-center h-44 rounded-lg bg-muted/50 mb-6">
        {isConnected && agent.canListen ? (
          <BarVisualizer
            track={agent.microphoneTrack}
            state={agent.state}
            barCount={7}
            style={{ width: '100%', height: '100%' }}
          />
        ) : isConnecting ? (
          <p className="text-sm text-muted-foreground">Verbinde...</p>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Icon className="size-8 text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">Bereit zum Anrufen</p>
          </div>
        )}
      </div>

      <Separator className="mb-4" />

      <div className="flex justify-center gap-3">
        {!isCallActive || isDisconnected ? (
          <Button onClick={handleStartCall} disabled={isConnecting} className="w-full">
            <Phone className="size-4" />
            Anrufen
          </Button>
        ) : (
          <>
            <MicToggle />
            <Button variant="destructive" onClick={handleEndCall}>
              <PhoneOff className="size-4" />
              Auflegen
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function MicToggle() {
  const { toggle, enabled } = useTrackToggle({
    source: Track.Source.Microphone,
  });

  return (
    <Button
      variant={enabled ? 'outline' : 'secondary'}
      size="default"
      onClick={() => toggle()}
    >
      {enabled ? (
        <>
          <Mic className="size-4" />
          Mikrofon an
        </>
      ) : (
        <>
          <MicOff className="size-4" />
          Mikrofon aus
        </>
      )}
    </Button>
  );
}

function getStateLabel(
  connectionState: ConnectionState,
  agentState: string | undefined
): string {
  if (connectionState === ConnectionState.Connecting) return 'Verbinde...';
  if (connectionState === ConnectionState.Reconnecting) return 'Verbinde neu...';
  if (connectionState === ConnectionState.Disconnected) return 'Nicht verbunden';

  switch (agentState) {
    case 'listening':
      return 'Hoert zu';
    case 'thinking':
      return 'Denkt nach...';
    case 'speaking':
      return 'Spricht';
    case 'connecting':
    case 'initializing':
      return 'Agent startet...';
    default:
      return 'Verbunden';
  }
}
